/**
 * Facet Filters Component - Handles collection filtering functionality
 * Manages filter form state, URL updates, and AJAX product grid updates
 * Supports both standard filtering and faceted navigation (collection switching)
 * Uses server-side rendered active filters for better performance
 */

import { Component } from "./component-base.js";

class FacetFiltersFormComponent extends HTMLElement {
  constructor() {
    super();
    this.inputTimeout = null;
    this.facetedMode = false; // Track if faceted navigation is enabled
    this.moreFiltersExpanded = false; // Track if more filters button is expanded
  }

  connectedCallback() {
    this.form = this.querySelector("form");

    if (!this.form) {
      console.warn('[FacetFilters] No form found in facet-filters-form');
      return;
    }

    // Check if faceted navigation is enabled via data attribute from Liquid setting
    this.facetedMode = this.form.getAttribute('data-faceted-nav-enabled') === 'true';

    // Bind initial event listeners
    this.#bindInitialEventListeners();

    // Initialize accordion behavior for filter dropdowns
    this.#initFilterAccordions();

    // Initialize more filters toggle
    this.#initMoreFiltersToggle();

    // Initialize click outside to close behavior for desktop
    this.#initClickOutsideToClose();

    window.addEventListener("popstate", () => {
      this.#restoreFiltersFromUrl();
      this.#fetchFilteredGrid(new URLSearchParams(window.location.search));
    });

    // 1. Restore filter UI
    this.#restoreFiltersFromUrl();

    // 2. Fetch matching products if URL has filters
    const params = new URLSearchParams(window.location.search);
    if (params.toString().length > 0) {
      this.#fetchFilteredGrid(params);
    }
  }

  disconnectedCallback() {
    // Clean up event listeners when component is removed
    if (this._clickOutsideHandler) {
      document.removeEventListener('click', this._clickOutsideHandler);
    }
  }

  /**
   * Binds initial event listeners to form elements
   */
  #bindInitialEventListeners() {
    // Bind change events with proper context - pass event for faceted mode
    this.boundHandleFilterChange = (event) => this.#handleFilterChange(event);

    this.form.addEventListener("change", this.boundHandleFilterChange);

    // Also bind input events for number/text inputs (for real-time filtering)
    const numberInputs = this.form.querySelectorAll('input[type="number"], input[type="text"]');
    numberInputs.forEach(input => {
      input.addEventListener('input', () => {
        // Debounce text/number inputs
        clearTimeout(this.inputTimeout);
        this.inputTimeout = setTimeout(() => {
          this.#handleFilterChange();
        }, 500);
      });
    });

    // Bind clear all filters button (both desktop and mobile)
    const clearAllBtns = document.querySelectorAll('[data-filters-clear-all]');
    clearAllBtns.forEach(clearAllBtn => {
      clearAllBtn.addEventListener('click', () => {
        this.#clearAllFilters();
      });
    });
  }



  /**
   * Clears all filters and resets the form
   */
  #clearAllFilters() {
    // Clear all form inputs
    this.form.querySelectorAll("input[type=checkbox]").forEach((el) => el.checked = false);
    this.form.querySelectorAll("input[type=number]").forEach((el) => el.value = '');
    this.form.querySelectorAll("input[type=radio]").forEach((el) => el.checked = false);
    this.form.querySelectorAll("input[type=text]").forEach((el) => el.value = '');

    // Update URL to remove all filters
    const newUrl = window.location.pathname;
    history.replaceState({}, '', newUrl);

    // Fetch clean results
    this.#fetchFilteredGrid(new URLSearchParams());
  }

  /**
   * Handles filter form changes by updating URL and fetching new results
   * @param {Event} event - The change event (optional)
   */
  #handleFilterChange(event = null) {
    const formData = new FormData(this.form);
    const params = new URLSearchParams();
    const targetUrl = window.location.pathname;

    // Add non-empty values to URL parameters
    for (const [key, value] of formData.entries()) {
      if (value && value.trim() !== '') {
        params.append(key, value);
      }
    }

    const urlString = params.toString();
    const newUrl = urlString ? `${targetUrl}?${urlString}` : targetUrl;

    history.replaceState({}, '', newUrl);
    this.#fetchFilteredGrid(params, targetUrl, targetUrl);
  }
  /**
   * Gets the dynamic section ID from the shopify-section wrapper element
   * @returns {string} The section ID without the 'section-' prefix
   */
  #getSectionId() {
    // Find the shopify-section element that wraps this filter form
    const sectionElement = this.closest('.shopify-section') || this.form?.closest('.shopify-section');

    if (sectionElement && sectionElement.id) {
      // Strip the 'section-' prefix from the ID
      // e.g., 'shopify-section-template--26025869312339__main' becomes 'template--26025869312339__main'
      return sectionElement.id.replace(/^shopify-section-/, '');
    }

    // Fallback to 'collection' if no section ID found
    console.warn('[FacetFilters] Could not find section ID, falling back to "collection"');
    return 'collection';
  }

  /**
   * Fetches filtered product grid via AJAX and updates specific DOM elements
   * @param {URLSearchParams} params - The filter parameters
   * @param {string} targetUrl - The target URL for faceted navigation (optional)
   * @param {string} originalPath - The original path before navigation (optional)
   */
  #fetchFilteredGrid(params, targetUrl = null, originalPath = null) {
    const sectionId = this.#getSectionId();
    const fetchUrl = targetUrl || window.location.pathname;

    fetch(`${fetchUrl}?section_id=${sectionId}&${params}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch filtered results');
        return res.text();
      })
      .then((html) => {
        const newDom = new DOMParser().parseFromString(html, 'text/html');

        // Determine if we're on a search page or collection page
        const isSearchPage = window.location.pathname.includes('/search');
        
        // Update product/results grid - support both collection and search pages
        const gridSelector = isSearchPage ? '#SearchResultsContainer' : '#ProductGridContainer';
        const newGrid = newDom.querySelector(gridSelector);
        const currentGrid = document.querySelector(gridSelector);

        if (newGrid && currentGrid) {
          currentGrid.innerHTML = newGrid.innerHTML;
        }

        // Update pagination - support both collection and search pages
        const paginationSelector = isSearchPage ? '#SearchPagination' : '#CollectionPagination';
        const newPagination = newDom.querySelector(paginationSelector);
        const currentPagination = document.querySelector(paginationSelector);

        if (newPagination && currentPagination) {
          currentPagination.outerHTML = newPagination.outerHTML;
        } else if (!newPagination && currentPagination) {
          // Hide pagination if no pages needed
          currentPagination.style.display = 'none';
        }

        // Update product count (desktop) - support both collection and search pages
        const productCountSelector = isSearchPage ? '#SearchProductCount' : '#CollectionProductCount';
        const newProductCount = newDom.querySelector(productCountSelector);
        const currentProductCount = document.querySelector(productCountSelector);

        if (newProductCount && currentProductCount) {
          currentProductCount.innerHTML = newProductCount.innerHTML;
        }

        // Update product count (mobile) - support both collection and search pages
        const productCountMobileSelector = isSearchPage ? '#SearchProductCountMobile' : '#CollectionProductCountMobile';
        const newProductCountMobile = newDom.querySelector(productCountMobileSelector);
        const currentProductCountMobile = document.querySelector(productCountMobileSelector);

        if (newProductCountMobile && currentProductCountMobile) {
          currentProductCountMobile.innerHTML = newProductCountMobile.innerHTML;
        }

        // Update product count (mobile drawer) - support both collection and search pages
        const productCountMobileDrawerSelector = isSearchPage ? '#SearchProductCountMobileDrawer' : '#CollectionProductCountMobileDrawer';
        const newProductCountMobileDrawer = newDom.querySelector(productCountMobileDrawerSelector);
        const currentProductCountMobileDrawer = document.querySelector(productCountMobileDrawerSelector);

        if (newProductCountMobileDrawer && currentProductCountMobileDrawer) {
          currentProductCountMobileDrawer.innerHTML = newProductCountMobileDrawer.innerHTML;
        }

        // Update sort text - support both collection and search pages
        const sortTextSelector = isSearchPage ? '#SearchSortText' : '#CollectionSortText';
        const newSortText = newDom.querySelector(sortTextSelector);
        const currentSortText = document.querySelector(sortTextSelector);

        if (newSortText && currentSortText) {
          currentSortText.innerHTML = newSortText.innerHTML;
        }

        // Update active filters area with server-rendered content
        this.#updateActiveFilters(newDom);

        // Update filter options - refresh the entire filter forms to get updated counts and availability
        // For faceted mode, update filters if collection changed
        if (this.facetedMode && targetUrl && targetUrl !== originalPath) {
          this.#updateFiltersFromResponse(newDom);
        } else {
          this.#updateFilterOptions(newDom);
        }

        // Dispatch custom event - for reinitialising additional js components
        // on collection/search grid updating
        const eventName = 'filtersUpdated';
        document.dispatchEvent(new CustomEvent(eventName, {
          detail: {
            container: currentGrid,
            pagination: document.querySelector(paginationSelector),
            productCount: currentProductCount,
            sortText: currentSortText,
            isSearchPage: isSearchPage
          }
        }));
      })
      .catch((error) => {
        console.error('Filter fetch failed:', error);
      });
  }

  /**
   * Updates active filters with server-rendered content
   * @param {Document} newDom - The parsed HTML response containing updated active filters
   */
  #updateActiveFilters(newDom) {
    // Update desktop active filters - using correct CSS selector for lg:block
    const newDesktopActiveFilters = newDom.querySelector('facet-filters-form.hidden.lg\\:block [id^="ActiveFilters-"]');
    const currentDesktopActiveFilters = document.querySelector('facet-filters-form.hidden.lg\\:block [id^="ActiveFilters-"]');

    if (newDesktopActiveFilters && currentDesktopActiveFilters) {
      currentDesktopActiveFilters.outerHTML = newDesktopActiveFilters.outerHTML;
    } else if (!newDesktopActiveFilters && currentDesktopActiveFilters) {
      // No active filters, remove the current ones
      currentDesktopActiveFilters.remove();
    } else if (newDesktopActiveFilters && !currentDesktopActiveFilters) {
      // New active filters, add them to the desktop form
      const desktopForm = document.querySelector('facet-filters-form.hidden.lg\\:block');
      if (desktopForm) {
        desktopForm.appendChild(newDesktopActiveFilters.cloneNode(true));
      }
    }

    // Update mobile active filters in drawer
    const newMobileActiveFilters = newDom.querySelector('filters-drawer-component [id^="ActiveFilters-"]');
    const currentMobileActiveFilters = document.querySelector('filters-drawer-component [id^="ActiveFilters-"]');

    if (newMobileActiveFilters && currentMobileActiveFilters) {
      currentMobileActiveFilters.outerHTML = newMobileActiveFilters.outerHTML;
    } else if (!newMobileActiveFilters && currentMobileActiveFilters) {
      // No active filters, remove the current ones
      currentMobileActiveFilters.remove();
    } else if (newMobileActiveFilters && !currentMobileActiveFilters) {
      // New active filters, add them to the mobile form
      const mobileForm = document.querySelector('filters-drawer-component facet-filters-form form');
      if (mobileForm) {
        // Insert after the form opening tag but before the sort section
        const sortSection = mobileForm.querySelector('.space-y-6 > div');
        if (sortSection) {
          mobileForm.insertBefore(newMobileActiveFilters.cloneNode(true), sortSection);
        } else {
          mobileForm.prepend(newMobileActiveFilters.cloneNode(true));
        }
      }
    }
  }

  /**
   * Updates filters from response when navigating between collections (faceted mode)
   * @param {Document} newDom - The new DOM from the response
   */
    #updateFiltersFromResponse(newDom) {
      // Store which filter dropdown is currently open
      const openDropdownId = this.#getCurrentOpenDropdown();
    
    // Update desktop filters
    const newDesktopFilters = newDom.querySelector('facet-filters-form.hidden.lg\\:block form filter-horizontal');
    const currentDesktopFilters = document.querySelector('facet-filters-form.hidden.lg\\:block form filter-horizontal');
    
    if (newDesktopFilters && currentDesktopFilters) {
      currentDesktopFilters.innerHTML = newDesktopFilters.innerHTML;
    }
    
    // Update mobile filters
    const newMobileFilters = newDom.querySelector('filters-drawer-component facet-filters-form');
    const currentMobileFilters = document.querySelector('filters-drawer-component facet-filters-form');
    
    if (newMobileFilters && currentMobileFilters) {
      const newFormContent = newMobileFilters.querySelector('form');
      const currentFormContent = currentMobileFilters.querySelector('form');
      if (newFormContent && currentFormContent) {
        currentFormContent.innerHTML = newFormContent.innerHTML;
      }
    }
    
    // Restore checkboxes from URL params (for both desktop and mobile)
    this.#restoreFiltersFromUrl();
    
    // Restore the open dropdown state
    if (openDropdownId) {
      this.#restoreOpenDropdown(openDropdownId);
    }
    
    // Re-bind event listeners
    this.#rebindEventListeners();
    
    // Update faceted mode detection from form data attribute
    this.facetedMode = this.form.getAttribute('data-faceted-nav-enabled') === 'true';
  }

  /**
   * Updates filter options with fresh data from the server response
   * @param {Document} newDom - The parsed HTML response containing updated filters
   */
  #updateFilterOptions(newDom) {

    // Store which filter dropdown is currently open
    const openDropdownId = this.#getCurrentOpenDropdown();

    // Update desktop filters - using correct CSS selector for lg:block
    const newDesktopFilters = newDom.querySelector('facet-filters-form.hidden.lg\\:block form filter-horizontal');
    const currentDesktopFilters = document.querySelector('facet-filters-form.hidden.lg\\:block form filter-horizontal');

    if (newDesktopFilters && currentDesktopFilters) {
      currentDesktopFilters.innerHTML = newDesktopFilters.innerHTML;
    }

    // Update mobile filters
    const newMobileFilters = newDom.querySelector('filters-drawer-component facet-filters-form');
    const currentMobileFilters = document.querySelector('filters-drawer-component facet-filters-form');

    if (newMobileFilters && currentMobileFilters) {
      // Update the entire form content to get updated filter options and counts
      const newFormContent = newMobileFilters.querySelector('form');
      const currentFormContent = currentMobileFilters.querySelector('form');
      if (newFormContent && currentFormContent) {
        currentFormContent.innerHTML = newFormContent.innerHTML;
      }
    }

    // Restore the open dropdown state
    this.#restoreOpenDropdown(openDropdownId);

    // Re-bind event listeners for the new elements
    this.#rebindEventListeners();
  }

  /**
   * Gets the ID of the currently open filter dropdown
   * @returns {string|null} The ID of the open dropdown or null if none are open
   */
  #getCurrentOpenDropdown() {
    // Check both desktop and mobile dropdowns
    const openDropdown = document.querySelector('[data-filter-dropdown][open]');
    return openDropdown ? openDropdown.id || openDropdown.getAttribute('data-filter-dropdown') : null;
  }


  /**
   * Restores the open state of a filter dropdown after DOM update
   * @param {string|null} dropdownId - The ID or data attribute value of the dropdown to restore
   */
  #restoreOpenDropdown(dropdownId) {
    if (!dropdownId) return;

    // Try to find the dropdown by ID first
    let dropdown = document.getElementById(dropdownId);

    // If not found by ID, try by data-filter-dropdown attribute
    if (!dropdown) {
      dropdown = document.querySelector(`[data-filter-dropdown="${dropdownId}"]`);
    }

    // If still not found, try a broader search for any dropdown with the ID in its attributes
    if (!dropdown) {
      dropdown = document.querySelector(`[data-filter-dropdown][id*="${dropdownId}"], [data-filter-dropdown*="${dropdownId}"]`);
    }

    if (dropdown) {
      // Small delay to ensure DOM is fully updated before opening
      setTimeout(() => {
        dropdown.open = true;
      }, 10);
    }
  }


  /**
   * Re-binds event listeners after DOM updates
   */
  #rebindEventListeners() {
    // Remove the main form change listener to avoid duplicates
    this.form.removeEventListener("change", this.boundHandleFilterChange);

    // Re-attach the main form change listener
    this.form.addEventListener("change", this.boundHandleFilterChange);

    // Re-attach input listeners for number/text inputs (for real-time filtering)
    const numberInputs = this.form.querySelectorAll('input[type="number"], input[type="text"]');
    numberInputs.forEach(input => {
      input.addEventListener('input', () => {
        // Debounce text/number inputs
        clearTimeout(this.inputTimeout);
        this.inputTimeout = setTimeout(() => {
          this.#handleFilterChange();
        }, 500);
      });
    });

    // Re-initialize any custom components if needed
    const mobileComponents = this.form.querySelectorAll('sorting-mobile-component');
    mobileComponents.forEach(component => {
      if (component.setupSortingListeners) {
        component.setupSortingListeners();
      }
    });

    // Re-initialize mobile drawer components if they exist
    const mobileDrawerComponents = document.querySelectorAll('filters-drawer-component sorting-mobile-component');
    mobileDrawerComponents.forEach(component => {
      if (component.setupSortingListeners) {
        component.setupSortingListeners();
      }
    });

    // Re-bind clear all filters buttons after DOM update
    const clearAllBtns = document.querySelectorAll('[data-filters-clear-all]');
    clearAllBtns.forEach(clearAllBtn => {
      // Remove existing listeners to avoid duplicates
      clearAllBtn.removeEventListener('click', clearAllBtn._clearAllHandler);

      // Create and store the handler function
      clearAllBtn._clearAllHandler = () => {
        this.#clearAllFilters();
      };

      // Add the event listener
      clearAllBtn.addEventListener('click', clearAllBtn._clearAllHandler);
    });

    // Re-initialize accordion behavior for filter dropdowns
    this.#initFilterAccordions();

    // Re-initialize more filters toggle
    this.#initMoreFiltersToggle();

    // Re-initialize click outside to close behavior
    this.#initClickOutsideToClose();
  }

  /**
   * Initializes accordion behavior for filter dropdowns
   * Ensures only one filter dropdown is open at a time
   */
  #initFilterAccordions() {
    // Target all filter dropdowns using the data attribute
    const allFilterDropdowns = document.querySelectorAll('[data-filter-dropdown]');

    allFilterDropdowns.forEach(dropdown => {
      // Remove existing listeners to avoid duplicates
      dropdown.removeEventListener('toggle', dropdown._accordionHandler);

      // Create and store the handler function
      dropdown._accordionHandler = function () {
        if (this.open) {
          // Add a small delay to avoid interfering with programmatic opening
          // This allows the restore function to complete before accordion behavior kicks in
          setTimeout(() => {
            // Close all other filter dropdowns when opening this one
            allFilterDropdowns.forEach(otherDropdown => {
              if (otherDropdown !== this && otherDropdown.open) {
                otherDropdown.open = false;
              }
            });
          }, 50);
        }
      };

      // Add the event listener
      dropdown.addEventListener('toggle', dropdown._accordionHandler);
    });
  }

  /**
   * Initializes the more filters toggle button
   * Handles showing/hiding additional filters beyond the first 3
   */
  #initMoreFiltersToggle() {
    const toggleButton = this.form?.querySelector('[data-more-filters-toggle]');
    
    if (!toggleButton) return;

    // Remove existing listener to avoid duplicates
    toggleButton.removeEventListener('click', toggleButton._moreFiltersHandler);

    // Create and store the handler function
    toggleButton._moreFiltersHandler = () => {
      this.#toggleMoreFilters();
    };

    // Add the event listener
    toggleButton.addEventListener('click', toggleButton._moreFiltersHandler);

    // Restore expanded state if it was previously expanded
    if (this.moreFiltersExpanded) {
      this.#setMoreFiltersState(true);
    }
  }

  /**
   * Toggles the visibility of additional filters
   */
  #toggleMoreFilters() {
    const sectionId = this.#getSectionId();
    const hiddenFiltersContainer = document.getElementById(`HiddenFilters-${sectionId}`);
    const toggleButton = document.getElementById(`MoreFiltersToggle-${sectionId}`);
    
    if (!hiddenFiltersContainer || !toggleButton) return;

    // Toggle the expanded state
    this.moreFiltersExpanded = !this.moreFiltersExpanded;
    this.#setMoreFiltersState(this.moreFiltersExpanded);
  }

  /**
   * Sets the more filters expanded/collapsed state
   * @param {boolean} expanded - Whether filters should be expanded
   */
  #setMoreFiltersState(expanded) {
    const sectionId = this.#getSectionId();
    const hiddenFiltersContainer = document.getElementById(`HiddenFilters-${sectionId}`);
    const toggleButton = document.getElementById(`MoreFiltersToggle-${sectionId}`);
    
    if (!hiddenFiltersContainer || !toggleButton) return;

    const moreText = toggleButton.querySelector('.more-filters-text');
    const lessText = toggleButton.querySelector('.less-filters-text');

    if (expanded) {
      // Show hidden filters
      hiddenFiltersContainer.style.display = 'flex';
      hiddenFiltersContainer.setAttribute('data-filters-expanded', 'true');
      toggleButton.setAttribute('aria-expanded', 'true');
      
      // Toggle text visibility
      if (moreText) moreText.style.display = 'none';
      if (lessText) lessText.style.display = 'inline';
    } else {
      // Hide filters
      hiddenFiltersContainer.style.display = 'none';
      hiddenFiltersContainer.setAttribute('data-filters-expanded', 'false');
      toggleButton.setAttribute('aria-expanded', 'false');
      
      // Toggle text visibility
      if (moreText) moreText.style.display = 'inline';
      if (lessText) lessText.style.display = 'none';
    }
  }

  /**
   * Initializes click outside to close behavior for desktop filter dropdowns
   * Closes dropdowns when clicking outside, but keeps them open when clicking inside
   */
  #initClickOutsideToClose() {
    // Remove existing listener to avoid duplicates
    if (this._clickOutsideHandler) {
      document.removeEventListener('click', this._clickOutsideHandler);
    }

    // Create and store the handler function
    this._clickOutsideHandler = (event) => {
      // Use setTimeout to ensure this runs after the native details toggle
      setTimeout(() => {
        // Get all open filter dropdowns (desktop only - not in mobile drawer)
        const desktopFilterForm = document.querySelector('facet-filters-form.desktop-filters');
        
        if (!desktopFilterForm) return;

        const openDropdowns = desktopFilterForm.querySelectorAll('[data-filter-dropdown][open]');
        
        openDropdowns.forEach(dropdown => {
          // Check if the click was outside this dropdown
          if (!dropdown.contains(event.target)) {
            dropdown.open = false;
          }
        });
      }, 0);
    };

    // Add the event listener to document
    document.addEventListener('click', this._clickOutsideHandler);
  }

  /**
   * Restores filter checkboxes based on current URL parameters to all filter forms
   */
  #restoreFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const allFilterForms = document.querySelectorAll('facet-filters-form form');

    // Clear filter inputs first in all forms, but preserve non-filter radio inputs (like sort_by)
    allFilterForms.forEach(form => {
      form.querySelectorAll("input[type=checkbox]").forEach((el) => el.checked = false);
      form.querySelectorAll("input[type=number]").forEach((el) => el.value = '');
      // Only clear radio inputs that are filter-related, not sort-related
      form.querySelectorAll("input[type=radio]:not([name='sort_by'])").forEach((el) => el.checked = false);
    });

    for (const [name, value] of params.entries()) {
      // Handle checkboxes and radio buttons (including sort_by) in all forms
      allFilterForms.forEach(form => {
        const checkboxOrRadio = form.querySelector(`[name="${name}"][value="${value}"]`);
        if (checkboxOrRadio) {
          checkboxOrRadio.checked = true;
        }

        // Handle number inputs (like price range)
        const numberInput = form.querySelector(`[name="${name}"]`);
        if (numberInput && (numberInput.type === 'number' || numberInput.type === 'text')) {
          numberInput.value = value;
        }
      });
    }
  }
}

/**
 * Initializes the filters component for collection pages
 * Should be called on collection template pages
 */
export async function initFilters() {
  if (!customElements.get("facet-filters-form")) {
    customElements.define("facet-filters-form", FacetFiltersFormComponent);
  }
}

